import { isAvatarSrc, thumbnailMediumBlue } from '@utils/thumbnails'
import { colors, fontSize, size } from '@styles/theme'
import { useAuthContext } from '@contexts/authContext'
import useDrawerContext from '@contexts/drawerContext'
import useModalContext from '@contexts/modalContext'
import { useNavigate } from 'react-router-dom'
import capitalize from '@utils/capitalize'
import useDialog from '@hooks/useDialog'

import { Drawer, List, ListItem, ListItemText, ListItemButton, ListItemIcon, Divider, Box, Button, IconButton, } from '@mui/material'
import { PiGearSixFill, PiGearSix, PiUserBold, PiWrenchBold, PiArrowRightBold, PiMapTrifoldBold } from 'react-icons/pi'
import { BackgroundImage } from '@components/auth/landing/LandingElements'
import NearbySettingsDialog from '@forms/profile_forms/MainProfileForm'
import NearbyStatusDialog from '@components/nearby/NearbyStatusDialog'
import NearbyLegendDialog from '@components/nearby/NearbyLegendDialog'
import ProfileNavigateAvatar from '@elements/ProfileNavigateAvatar'
import { StatusIcon } from '@elements/StatusIndicator'

const NavListItem = ({ Icon, text, primaryTypographyPropsText, graphicBackground=false, onClick, secondaryAction, listItemStyles, buttonProps }) => {
  return (
    <ListItem
      slotProps={{ root: { style: listItemStyles } }}
      sx={listItemStyles}
      secondaryAction={secondaryAction}>
      {graphicBackground && <BackgroundImage style={{ filter: 'grayscale(1) brightness(0.15)', borderRadius: '0 15px 15px 0' }} src='https://mandrake-app.b-cdn.net/dev/application/display_elements/display-background-4-480w.jpeg' />}
      <ListItemButton
        onClick={onClick}
        style={{ fontSize: fontSize('lg') }}
        disableRipple
        {...buttonProps}>
        {Icon &&
          <ListItemIcon>
            <Icon fill='darkgrey' size={size(3)} />
          </ListItemIcon>
        }
        <ListItemText
          slotProps={{
            primary: {
              sx: { 
                fontWeight: 'bold',
                letterSpacing: size(0.1),
                paddingLeft: size(1),
                ...primaryTypographyPropsText
              }
            }
          }}
          primary={text}
        />
      </ListItemButton>
    </ListItem>
  )
}

const LeftNavDrawer = ({  }) => {
  const { closeLeftDrawer, isLeftDrawerOpen } = useDrawerContext()
  const { registerUser } = useModalContext()
  const navigate = useNavigate()
  
  const { state: authState } = useAuthContext()
  const { user } = authState || {}
  
  const [isNearbySettingsOpen, openNearbySettings, closeNearbySettings] = useDialog('nearby_profile_settings_sidebar', 'nearby-profile-settings-sidebar-open', { params: { form_display: 'nearby-social' } })
  const [isStatusDialogOpen, openStatusDialog, closeStatusDialog] = useDialog('nearby_status_sidebar', 'nearby-status-sidebar-open')
  const [isLegendDialogOpen, openLegendDialog, closeLegendDialog] = useDialog('nearby_legend', 'nearby-legend-open')

  const nearbyIsAvatar = isAvatarSrc('medium', user?.nearbyMedia?.thumbnails?.[0], user?.defaultAvatar)
  const nearbyThumbnail = thumbnailMediumBlue(user?.nearbyMedia?.thumbnails?.[0], user?.defaultAvatar)
  const socialIsAvatar = isAvatarSrc('medium', user?.socialMedia?.thumbnail, user?.defaultAvatar)
  const socialThumbnail = thumbnailMediumBlue(user?.socialMedia?.thumbnail, user?.defaultAvatar)

  const navigateToRegisterOrSubscription = () => {
    if (user?.isAnonymous) {
      registerUser.open({ search: { register_before_subscribe: true } })
    } else {
      navigate('/subscription')
    }
  }

  const renderAvatar = (userThumbnail, isAvatar, uuid, toNearby) => (
    <ProfileNavigateAvatar
      src={userThumbnail}
      toNearby={toNearby}
      uuid={uuid}
      style={{
        height: size(6),
        width: size(6),
        filter: isAvatar && 'grayscale(100%) contrast(120%)',
        border: '1px solid rgba(255,255,255,0.25)',
      }}
    />
  )

  const renderList = () => (
    <Box
      sx={{ overflowY: 'scroll' }}
      role="presentation"
      onKeyDown={closeLeftDrawer}>
      <List style={{ backgroundColor: 'black' }}>
        <NavListItem
          Icon={() => renderAvatar(nearbyThumbnail, nearbyIsAvatar, user?.nearbyUuid, true)}
          text={
            <>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: fontSize('lg') }}>Nearby Profile</div>
              <div style={{ color: 'grey', fontSize: fontSize('sm'), fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.extendedInfos?.tagline || <em>No Tagline</em>}
              </div>
            </>
          }
          onClick={() => navigate(`/map?screen_modal=nearby-profile&nearby_uuid=${user?.nearbyUuid}`)}
          primaryTypographyPropsText={{ fontSize: fontSize('md'), fontWeight: 'bold', lineHeight: size(2.5) }}
          secondaryAction={
            <IconButton onClick={() => openNearbySettings({ form_display: 'nearby' })}>
              <PiGearSix fill='white' size={size(2.75)} />
            </IconButton>
          }
        />
        {!authState?.user?.isAnonymous &&
          <NavListItem
            Icon={() => renderAvatar(socialThumbnail, socialIsAvatar, user?.uuid, false)}
            text={
              <>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: fontSize('lg') }}>Social Profile</div>
                <div style={{ color: 'grey', fontSize: fontSize('sm'), fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  @{user?.handle}
                </div>
              </>
            }
            onClick={() => navigate(`/profiles/${user?.uuid}`) }
            primaryTypographyPropsText={{ fontSize: fontSize('md'), fontWeight: 'bold', lineHeight: size(2.5) }}
            secondaryAction={
              <IconButton onClick={() => openNearbySettings({ form_display: 'social' })}>
                <PiGearSix fill='white' size={size(2.75)} />
              </IconButton>
            }
          />
        }
      </List>
      <Divider sx={{ border: '0.5px solid rgba(255,255,255,0.25)', width: 'calc(100% - 2rem)', margin: 'auto' }}  />
      <List style={{ backgroundColor: 'black', paddingTop: size(3) }}>
        {user?.isAnonymous &&
          <NavListItem
            listItemStyles={{ height: size(7), paddingLeft: `${size(3.5)} !important` }}
            primaryTypographyPropsText={{ paddingLeft: size(1) }}
            onClick={() => registerUser.open()}
            Icon={PiUserBold}
            text='Get Registered'
          />
        }
        <NavListItem
          listItemStyles={{ height: size(7), paddingLeft: `${size(3.5)} !important` }}
          primaryTypographyPropsText={{ paddingLeft: size(1) }}
          onClick={() => openNearbySettings()}
          Icon={PiWrenchBold}
          text='Edit Profiles'
        />
        <NavListItem
          listItemStyles={{ height: size(7), paddingLeft: `${size(3.5)} !important` }}
          primaryTypographyPropsText={{ paddingLeft: size(1), lineHeight: 1.25 }}
          onClick={() => openStatusDialog()}
          Icon={({ size }) => <StatusIcon status={user?.tempStatus} size={size} />}
          text={<span style={{ fontSize: fontSize('xs') }}>Looking Status:<br /><em style={{ fontSize: fontSize('md'), lineHeight: 1 }}>{capitalize(user?.tempStatus?.replaceAll('_', ' ') || 'None')}</em></span>}
        />
        <NavListItem
          listItemStyles={{ height: size(7), paddingLeft: `${size(3.5)} !important` }}
          primaryTypographyPropsText={{ paddingLeft: size(1) }}
          onClick={() => openLegendDialog()}
          Icon={PiMapTrifoldBold}
          text='Map Information'
        />
        <div style={{ height: size(3) }} />
        {!user?.isSubscribed &&
          <NavListItem
            onClick={() => navigateToRegisterOrSubscription()}
            primaryTypographyPropsText={{ fontSize: fontSize('lg') }}
            graphicBackground
            listItemStyles={{ backgroundColor: '#141414', borderRadius: '0', padding: '0.25rem 0 0.25rem 0', border: `0.5px solid #333333`, borderLeft: 'none' }}
            text={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', }}>
                <div style={{ fontSize: `${fontSize('lg')} !important` }}>Mandrake Supporter +</div>
                <div style={{ height: size(2), width: '100%' }} />
                <div style={{ display: 'flex', width: '100%', padding: `0 ${size(1.5)}` }} >
                  <Button
                    variant='contained'
                    style={{
                      backgroundColor: colors.statusColors.browsing('lightRgba')(0.5),
                      border: `2px solid ${colors.statusColors.browsing('primary')}`,
                      whiteSpace: 'nowrap',
                      borderRadius: '100px',
                      flexGrow: 1,
                    }}>
                    $3.99/mo
                  </Button>
                  {/* &nbsp;
                  <Button
                    variant='contained'
                    style={{
                      backgroundColor: colors.statusColors.right_now('lightRgba')(0.5),
                      border: `2px solid ${colors.statusColors.right_now('primary')}`,
                      whiteSpace: 'nowrap',
                      borderRadius: '100px',
                      flexGrow: 1,
                    }}>
                    $10.99/3mo
                  </Button> */}
                </div>
                <div style={{
                  fontSize: fontSize('xs'),
                  fontWeight: 400,
                  textAlign: 'center',
                  // backgroundColor: '#333333',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  // backgroundColor: colors.statusColors.chatting('lightRgba')(0.5),
                  // border: `1px solid ${colors.statusColors.chatting('primary')}`,
                  borderRadius: '15px',
                  margin: size(1),
                  padding: size(1.5),
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  <span><b><i>Mandrake</i></b> is a <i>very</i> new application. Consider supporting us to see it grow ☕️🐣❤️</span>
                  &nbsp;
                  <PiArrowRightBold size={size(2)} style={{ display: 'inline-block', flexShrink: 0 }} />
                </div>
              </div>
            }
          />
        }
      </List>
      {/* Clearfix for absolute positioned bottom settings */}
      <div style={{ height: size(10), width: '100%' }} />
    </Box>
  )

  return (
    <>
      <NearbySettingsDialog
        isOpen={isNearbySettingsOpen}
        setClosed={closeNearbySettings}
        // afterSubmit={updateOwnUser}
        // setUser={setUser}
      />
      <NearbyStatusDialog
        isOpen={isStatusDialogOpen}
        setClosed={closeStatusDialog}
      />
      <NearbyLegendDialog
        isOpen={isLegendDialogOpen}
        setClosed={closeLegendDialog}
      />
      <Drawer
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          },
          paper: {
            style: { backgroundColor: 'black', minWidth: '80dvw', maxWidth: '85dvw', overflow: 'hidden' }
          }
        }}
        anchor='left'
        open={isLeftDrawerOpen}
        onClose={closeLeftDrawer}>
        {renderList()}
        <div style={{ position: 'absolute', bottom: 0, width: '100%', backgroundColor: 'black' }}>
          <Divider sx={{ border: '0.5px solid rgba(255,255,255,0.25)', width: 'calc(100% - 2rem)', margin: `0 auto` }} />
          <NavListItem
            // style={{ paddingBottom: size(1), display: 'inline-block'}}
            listItemStyles={{ height: size(7), paddingLeft: `${size(3.5)} !important`, paddingBottom: size(1.25) }}
            primaryTypographyPropsText={{ paddingLeft: size(1) }}
            onClick={() => navigate('/settings')}
            Icon={PiGearSixFill}
            text='Account Settings'
          />
        </div>
      </Drawer>
    </>
  )
}

export default LeftNavDrawer
